import { FormControl, FormHelperText, InputLabel, Select, MenuItem } from '@mui/material'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'

/**
 * @description Renders a select input field with form control and error handling.
 *
 * @author Maria Eduarda Franke
 * @param {string} props.name - The name of the select input field.
 * @param {string} props.label - The label for the select input field.
 * @param {Array} props.items - The array of items to populate the select options.
 * @param {string} props.itemId - The key of the item to be used as the value for each option.
 * @param {Object} props.control - The control object from react-hook-form.
 * @param {Object} props.errors - The errors object from react-hook-form.
 * @returns {JSX.Element} The rendered select input field.
 */
const FormSelect = ({ name, label, items, itemId, control, errors, ...props }) => {

    const renderSelect = (field) => (
        <Select
            label={label}
            {...field}
        >
            {items.map((item) => (
                <MenuItem key={item[itemId]} value={item[itemId]}>
                    {item.name}
                </MenuItem>
            ))}
        </Select>
    )

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <FormControl
                    error={!!errors}
                    {...props}
                >
                    <InputLabel>{label}</InputLabel>
                    {renderSelect(field)}
                    <FormHelperText error={!!errors}>{errors?.message}</FormHelperText>
                </FormControl>
            )}
        />
    )
}

FormSelect.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    itemId: PropTypes.string.isRequired,
    control: PropTypes.object,
    errors: PropTypes.object
}

export default FormSelect
